import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LogIn, Eye, EyeOff, AlertCircle, UserPlus, FolderKanban, Sun, CircuitBoard, Wrench, IndianRupee, Sparkles, ShieldCheck, Mail, Lock } from 'lucide-react';
import { settingsAPI, authAPI } from '../services/api';
import ForgotPasswordModal from '../components/ForgotPasswordModal';

// Department to Dashboard route mapping
const DEPARTMENT_ROUTES = {
  'SALES': '/sales',
  'Sales': '/sales',
  'ACCOUNTS': '/accounts',
  'Accounts': '/accounts',
  'HR': '/hr',
  'Hr': '/hr',
  'FINANCE': '/finance',
  'Finance': '/finance',
  'PURCHASE': '/purchase',
  'Purchase': '/purchase',
  'EXPORTS': '/exports',
  'Exports': '/exports',
  'OPERATIONS': '/operations',
  'Operations': '/operations',
  'PROJECTS': '/',
  'Projects': '/',
  'Management': '/',
};

const getDepartmentRoute = (department, role) => {
  if (role === 'super_admin' || role === 'admin') {
    return '/';
  }
  if (department && DEPARTMENT_ROUTES[department]) {
    return DEPARTMENT_ROUTES[department];
  }
  return '/';
};

const HUBS = [
  { id: 'projects', name: 'Electrical EPC & Projects', icon: FolderKanban, color: 'from-amber-500 to-yellow-400', badge: 'Active Hub', delay: '0s', duration: '10s' },
  { id: 'solar', name: 'Solar & Green Energy', icon: Sun, color: 'from-emerald-400 to-teal-500', badge: 'AI Optimized', delay: '1.4s', duration: '11s' },
  { id: 'automation', name: 'Power Automation & SCADA', icon: CircuitBoard, color: 'from-cyan-400 to-blue-500', badge: 'Live Telemetry', delay: '2.8s', duration: '9.5s' },
  { id: 'amc', name: 'AMC & Field Services', icon: Wrench, color: 'from-purple-400 to-indigo-500', badge: 'Smart Dispatch', delay: '4.2s', duration: '10.5s' },
  { id: 'finance', name: 'Finance & Accounts', icon: IndianRupee, color: 'from-blue-400 to-indigo-600', badge: 'Real-time ERP', delay: '5.6s', duration: '9s' },
];

const KEYFRAMES = `
  @keyframes floatY {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-5px); }
  }
  @keyframes driftMesh1 {
    0%, 100% { transform: translate(0, 0) scale(1); }
    50% { transform: translate(40px, -30px) scale(1.15); }
  }
  @keyframes driftMesh2 {
    0%, 100% { transform: translate(0, 0) scale(1); }
    50% { transform: translate(-50px, 40px) scale(1.1); }
  }
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(200%); }
  }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(18px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes nodePulse {
    0%, 100% { opacity: 0.25; transform: scale(1); }
    50% { opacity: 0.9; transform: scale(1.6); }
  }
  @keyframes chipGlow {
    0%, 100% { box-shadow: 0 0 0 rgba(59, 130, 246, 0); }
    50% { box-shadow: 0 4px 22px rgba(59, 130, 246, 0.18); }
  }
  .login-fade-up { animation: fadeUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) both; }
  .login-fade-in { animation: fadeIn 1.2s ease-out both; }
  .hub-float { animation: floatY 10s ease-in-out infinite, chipGlow 10s ease-in-out infinite; }
  .mesh-1 { animation: driftMesh1 18s ease-in-out infinite; }
  .mesh-2 { animation: driftMesh2 22s ease-in-out infinite; }
  .node-dot { animation: nodePulse 4s ease-in-out infinite; }
  .btn-shimmer { position: relative; overflow: hidden; }
  .btn-shimmer::after {
    content: '';
    position: absolute;
    top: 0; left: 0;
    width: 40%; height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent);
    transform: translateX(-100%);
  }
  .btn-shimmer:hover::after { animation: shimmer 1.1s ease-out; }
`;

const Login = () => {
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [orgSettings, setOrgSettings] = useState({ name: '', logo_url: null });
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [checkingSetup, setCheckingSetup] = useState(true);

  // Mouse-follow glow state
  const cardRef = useRef(null);
  const [glow, setGlow] = useState({ x: -400, y: -400 });

  const handleCardMouseMove = useCallback((e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    setGlow({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  }, []);

  useEffect(() => {
    loadOrgSettings();
    checkSetupStatus();
  }, []);

  const checkSetupStatus = async () => {
    try {
      const res = await authAPI.check();
      setNeedsSetup(res.data.needs_setup);
    } catch (error) {
      console.error('Error checking setup status:', error);
    } finally {
      setCheckingSetup(false);
    }
  };

  const loadOrgSettings = async () => {
    try {
      const res = await settingsAPI.getOrganization();
      setOrgSettings(res.data);
    } catch (error) {
      console.error('Error loading org settings:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const trimmedEmail = email.trim().toLowerCase();
    const trimmedPassword = password.trim();

    if (!trimmedEmail || !trimmedPassword) {
      setError('Please enter both email and password');
      setLoading(false);
      return;
    }

    try {
      let result;
      if (needsSetup) {
        if (trimmedPassword !== confirmPassword.trim()) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }
        if (trimmedPassword.length < 6) {
          setError('Password must be at least 6 characters');
          setLoading(false);
          return;
        }
        result = await register(trimmedEmail, name.trim(), trimmedPassword);
      } else {
        result = await login(trimmedEmail, trimmedPassword);
      }

      const userData = result?.user;
      const redirectPath = getDepartmentRoute(userData?.department, userData?.role);
      navigate(redirectPath);
    } catch (error) {
      console.error('Login error:', error);
      const errorMsg = error.response?.data?.detail;
      if (errorMsg) {
        setError(errorMsg);
      } else if (error.message === 'Network Error') {
        setError('Network error. Please check your internet connection.');
      } else if (needsSetup) {
        setError('Registration failed. Please try again.');
      } else {
        setError('Login failed. Please check your email and password.');
      }
    } finally {
      setLoading(false);
    }
  };

  const logoUrl = orgSettings.logo_url
    ? `${window.location.origin}/api${orgSettings.logo_url}`
    : null;

  // Brand lockup: Enerzia company wordmark (with lightning bolt) on a white pill
  // so the orange text + black tagline stay legible on the dark card.
  const wordmarkUrl = '/enerzia-wordmark.png';

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#0B0F19] flex flex-col justify-between selection:bg-blue-500/30 selection:text-blue-200">
      <style>{KEYFRAMES}</style>

      {/* Ambient mesh background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="mesh-1 absolute w-[55vw] h-[55vw] rounded-full blur-[120px] opacity-25 bg-blue-600 top-[-15%] left-[-10%]" />
        <div className="mesh-2 absolute w-[50vw] h-[50vw] rounded-full blur-[120px] opacity-20 bg-purple-700 bottom-[-20%] right-[-8%]" />
        <div className="absolute w-[35vw] h-[35vw] rounded-full blur-[100px] opacity-15 bg-cyan-500 top-[35%] left-[30%]" />
        {/* Grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(148,163,184,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.5) 1px, transparent 1px)',
            backgroundSize: '56px 56px',
          }}
        />
        {/* Neural pulse nodes */}
        {[
          { top: '12%', left: '18%', delay: '0s' },
          { top: '28%', left: '82%', delay: '1s' },
          { top: '70%', left: '12%', delay: '2s' },
          { top: '82%', left: '70%', delay: '0.6s' },
          { top: '45%', left: '92%', delay: '1.6s' },
          { top: '8%', left: '55%', delay: '2.4s' },
        ].map((n, i) => (
          <span
            key={i}
            className="node-dot absolute w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-[0_0_12px_rgba(34,211,238,0.9)]"
            style={{ top: n.top, left: n.left, animationDelay: n.delay }}
          />
        ))}
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
        <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">

          {/* Smart Hub side panel (desktop only) */}
          <div className="hidden lg:flex lg:col-span-5 flex-col justify-center space-y-6 pr-4">
            <div className="login-fade-up space-y-3" style={{ animationDelay: '0.1s' }}>
              <div className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-3 py-1">
                <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
                <span className="text-[11px] font-semibold tracking-widest uppercase text-cyan-300">
                  AI-Powered Enterprise OS
                </span>
              </div>
              <h2 className="text-3xl xl:text-4xl font-extrabold tracking-tight text-white leading-tight">
                One hub.
                <br />
                Every workflow.
                <br />
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400">
                  Infinite intelligence.
                </span>
              </h2>
              <p className="text-sm text-slate-400 max-w-sm leading-relaxed">
                Smarthub Enerzia unifies your projects, AMC services, finance and sales into a single neural command center — now with a built-in AI assistant.
              </p>
            </div>

            {/* Floating smart hub chips — gentle drift + soft glow, no shaking */}
            <div className="space-y-3">
              {HUBS.map((hub, i) => {
                const Icon = hub.icon;
                return (
                  <div key={hub.id} className="login-fade-up" style={{ animationDelay: `${0.2 + i * 0.12}s` }}>
                    <div
                      data-testid={`hub-chip-${hub.id}`}
                      className="hub-float flex items-center gap-3 w-fit rounded-xl border border-slate-800/80 bg-slate-900/60 backdrop-blur-md px-4 py-2.5 hover:border-blue-500/40 hover:bg-slate-800/60 transition-colors duration-300"
                      style={{ animationDelay: hub.delay, animationDuration: `${hub.duration}, ${hub.duration}` }}
                    >
                      <div className={`h-9 w-9 rounded-lg bg-gradient-to-br ${hub.color} flex items-center justify-center shadow-lg`}>
                        <Icon className="text-white w-[18px] h-[18px]" />
                      </div>
                      <div className="leading-tight">
                        <div className="text-sm font-semibold text-slate-200">{hub.name}</div>
                        <div className="text-[11px] text-slate-500">{hub.badge}</div>
                      </div>
                      <span className="ml-3 h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.9)]" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Glass login card */}
          <div className="col-span-1 lg:col-span-7 w-full max-w-md mx-auto">
            <div
              ref={cardRef}
              onMouseMove={handleCardMouseMove}
              data-testid="login-card"
              className="login-fade-up backdrop-blur-2xl bg-slate-900/75 border border-slate-800/80 rounded-2xl shadow-[0_0_50px_rgba(15,23,42,0.8)] p-6 sm:p-8 relative overflow-hidden transition-all duration-300 hover:border-slate-700/80 hover:shadow-[0_0_40px_rgba(59,130,246,0.15)]"
              style={{ animationDelay: '0.25s' }}
            >
              {/* Mouse-follow glow */}
              <div
                className="pointer-events-none absolute inset-0 transition-opacity duration-200"
                style={{
                  background: `radial-gradient(320px circle at ${glow.x}px ${glow.y}px, rgba(59,130,246,0.12), transparent 70%)`,
                }}
                aria-hidden="true"
              />

              {/* Logo / Header */}
              <div className="relative text-center mb-7">
                <img
                  src={wordmarkUrl}
                  alt="Enerzia — Think Smarter Go Greener"
                  data-testid="login-org-logo"
                  className="h-14 sm:h-16 w-auto mx-auto mb-4 object-contain drop-shadow-[0_0_22px_rgba(249,115,22,0.35)]"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = logoUrl || '/logo.png';
                  }}
                />
                <h1 data-testid="login-page-title" className="text-2xl font-extrabold tracking-tight text-white">
                  Smarthub Enerzia
                </h1>
                <p className="text-sm text-slate-400 mt-1.5">
                  {needsSetup ? 'Create your admin account to get started' : 'Sign in to your neural workspace'}
                </p>
              </div>

              {/* First-time Setup Info */}
              {needsSetup && (
                <div
                  data-testid="login-setup-banner"
                  className="relative mb-5 p-3.5 rounded-lg border bg-blue-500/10 border-blue-500/30 text-blue-300"
                >
                  <p className="text-sm">
                    <strong>First-time setup:</strong> This account will be the administrator with full access to manage users and settings.
                  </p>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div
                  data-testid="login-error-banner"
                  className="relative mb-5 p-3.5 rounded-lg border bg-red-500/10 border-red-500/30 flex items-center gap-3 text-red-300"
                >
                  <AlertCircle size={18} className="shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="relative space-y-5">
                {needsSetup && (
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="John Doe"
                      required
                      data-testid="register-name-input"
                      className="w-full px-4 py-3 rounded-lg bg-slate-800/60 border border-slate-700/60 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all duration-300"
                      style={{ fontSize: '16px' }}
                    />
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-2">
                    Email Address
                  </label>
                  <div className="relative group">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-slate-500 group-focus-within:text-blue-400 transition-colors duration-300" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@enerzia.com"
                      required
                      autoComplete="email"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck="false"
                      data-testid="login-email-input"
                      className="w-full pl-11 pr-4 py-3 rounded-lg bg-slate-800/60 border border-slate-700/60 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all duration-300"
                      style={{ fontSize: '16px' }}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-300">
                      Password
                    </label>
                    {!needsSetup && (
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        data-testid="login-forgot-password-button"
                        className="text-xs text-cyan-400 hover:text-cyan-300 font-semibold transition-colors duration-200"
                      >
                        Forgot Password?
                      </button>
                    )}
                  </div>
                  <div className="relative group">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-slate-500 group-focus-within:text-blue-400 transition-colors duration-300" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      autoComplete="current-password"
                      autoCapitalize="none"
                      autoCorrect="off"
                      data-testid="login-password-input"
                      className="w-full pl-11 pr-12 py-3 rounded-lg bg-slate-800/60 border border-slate-700/60 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all duration-300"
                      style={{ fontSize: '16px' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      data-testid="login-toggle-password-visibility"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-blue-400 transition-colors duration-200"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {needsSetup && (
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-2">
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      data-testid="register-confirm-password-input"
                      className="w-full px-4 py-3 rounded-lg bg-slate-800/60 border border-slate-700/60 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all duration-300"
                      style={{ fontSize: '16px' }}
                    />
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || checkingSetup}
                  data-testid="login-submit-button"
                  className="btn-shimmer w-full py-3 rounded-lg font-semibold text-sm tracking-wide text-white bg-gradient-to-br from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 shadow-[0_8px_24px_rgba(37,99,235,0.35)] hover:shadow-[0_8px_32px_rgba(37,99,235,0.5)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading || checkingSetup ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : needsSetup ? (
                    <>
                      <UserPlus size={18} />
                      Create Admin Account
                    </>
                  ) : (
                    <>
                      <LogIn size={18} />
                      Sign In
                    </>
                  )}
                </button>
              </form>

              {/* Security hint */}
              <div className="relative mt-6 flex items-center justify-center gap-1.5 text-[11px] text-slate-500">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
                <span>Secured with enterprise-grade encryption</span>
              </div>
            </div>

            <p className="text-center text-sm text-slate-500 mt-6">
              Contact your administrator if you need access
            </p>
          </div>
        </div>
      </div>

      {/* Copyright Footer */}
      <footer className="relative z-10 py-4 text-center">
        <p data-testid="login-footer-copyright" className="text-xs text-slate-500 tracking-wider font-mono">
          COPYRIGHT © 2026 Smarthub Enerzia, All rights Reserved
        </p>
      </footer>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />
    </div>
  );
};

export default Login;
